import { setGuideEnabled } from "./guide-preference";
import type { CodeMapCompilation } from "../../shared/codemap";
import type { QueueSnapshot, SyncRunView } from "../../shared/global-queue";
import type { ReviewGuide } from "../../shared/guide";
import type { ReviewEvent } from "../../shared/progress";
import { githubReviewEventFor, type ReviewAccess } from "../../shared/review-access";
import { draftAnchorSpan, type DraftAnchor, type DraftCommentKind } from "./db";
import { forgetLastKnownGood, readLastKnownGood, rememberLastKnownGood } from "./last-known-good";

export class HostedApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    readonly retryable = status === 429 || status >= 500,
  ) {
    super(message);
    this.name = "HostedApiError";
  }
}

export function hostedAuthenticationRequired(reason: unknown): reason is HostedApiError {
  return reason instanceof HostedApiError && reason.status === 401;
}

export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, init);
  const payload = await response.json().catch(() => undefined) as
    | { error?: { code?: string; message?: string; requestId?: string; retryable?: boolean } }
    | undefined;
  if (!response.ok) {
    throw new HostedApiError(
      response.status,
      payload?.error?.code ?? "hosted_request_failed",
      payload?.error?.message ?? `CHEW request failed (${response.status}).`,
      payload?.error?.requestId ?? response.headers.get("x-chew-request-id") ?? undefined,
      payload?.error?.retryable,
    );
  }
  return payload as T;
}

export interface HostedSession {
  guideEnabled?: boolean;
  user: { id: number; login: string; avatarUrl: string | null };
  expiresAt: string;
  csrfToken: string;
  organization: string;
  terraformRepositories?: string[];
}

let sessionPromise: Promise<HostedSession> | undefined;
const listCacheMilliseconds = 30_000;

export function hostedSession(): Promise<HostedSession> {
  sessionPromise ??= apiJson<HostedSession>("/api/session").then((session) => {
    setGuideEnabled(session.guideEnabled !== false);
    return session;
  }).catch((error: unknown) => {
    sessionPromise = undefined;
    throw error;
  });
  return sessionPromise;
}

export type HostedQueueSnapshot = QueueSnapshot;

let queueCacheRevision = 0;
let queueRequest: { revision: number; promise: Promise<HostedQueueSnapshot> } | undefined;
let queueSnapshot: { value: HostedQueueSnapshot; expiresAt: number } | undefined;
const locallyCompletedQueueHeads = new Set<string>();
const staleSinceByFeed = new Map<string, string>();

function queueHeadKey(repository: string, pullNumber: number, headSha: string): string {
  return `${repository.toLocaleLowerCase()}#${pullNumber}@${headSha.toLocaleLowerCase()}`;
}

function applyLocalQueueCompletions(snapshot: HostedQueueSnapshot): HostedQueueSnapshot {
  let changed = false;
  const entries = snapshot.entries.map((entry) => {
    if (!locallyCompletedQueueHeads.has(queueHeadKey(entry.repository, entry.pullNumber, entry.headSha))) return entry;
    changed = true;
    return {
      ...entry,
      forMe: {
        outcome: "ineligible" as const,
        reasonCode: "chew-review-current",
        detail: "You completed a CHEW review for this current head.",
        skipped: false,
        eligibleRank: null,
      },
    };
  });
  return changed ? { ...snapshot, entries } : snapshot;
}

export function invalidateHostedQueueSnapshot(): void {
  queueCacheRevision += 1;
  queueSnapshot = undefined;
}

/** When set, the named feed's data came from the last successful poll rather than the network. */
export function hostedDataStaleSince(feed: "queue" | "inbox"): string | undefined {
  return staleSinceByFeed.get(feed);
}

// The server answering with an error is not the same as being unreachable: a 401 must still fall
// through to the sign-in screen rather than quietly serving yesterday's queue.
function unreachable(reason: unknown): boolean {
  return !(reason instanceof HostedApiError);
}

function servedFresh(feed: "queue" | "inbox"): void {
  staleSinceByFeed.delete(feed);
}

function servedStale(feed: "queue" | "inbox", storedAt: string): void {
  staleSinceByFeed.set(feed, storedAt);
}

export async function loadHostedQueueSnapshot(options: { refresh?: boolean } = {}): Promise<HostedQueueSnapshot> {
  if (!options.refresh && queueSnapshot && queueSnapshot.expiresAt > Date.now()) return queueSnapshot.value;
  const revision = queueCacheRevision;
  if (queueRequest?.revision === revision) return queueRequest.promise;
  let requestPromise: Promise<HostedQueueSnapshot>;
  requestPromise = hostedSession()
    .then(async (session) => {
      try {
        const response = await apiJson<HostedQueueSnapshot>("/api/queue");
        rememberLastKnownGood(session.user.login, "queue", response);
        const visible = applyLocalQueueCompletions(response);
        if (queueCacheRevision === revision) {
          queueSnapshot = { value: visible, expiresAt: Date.now() + listCacheMilliseconds };
        }
        servedFresh("queue");
        return visible;
      } catch (reason: unknown) {
        if (hostedAuthenticationRequired(reason)) forgetLastKnownGood();
        const remembered = unreachable(reason)
          ? readLastKnownGood<HostedQueueSnapshot>(session.user.login, "queue")
          : undefined;
        if (!remembered) throw reason;
        servedStale("queue", remembered.storedAt);
        return applyLocalQueueCompletions(remembered.value);
      }
    })
    .finally(() => {
      if (queueRequest?.promise === requestPromise) queueRequest = undefined;
    });
  queueRequest = { revision, promise: requestPromise };
  return requestPromise;
}

export async function reconcileHostedQueue(): Promise<{ runId: string; status: "queued" }> {
  const session = await hostedSession();
  return apiJson<{ runId: string; status: "queued" }>("/api/queue/reconcile", {
    method: "POST",
    headers: { "x-chew-csrf": session.csrfToken },
  });
}

export function loadHostedSyncRun(runId: string): Promise<SyncRunView> {
  return hostedSession().then(() => apiJson<SyncRunView>(`/api/sync-runs/${encodeURIComponent(runId)}`));
}

export async function setHostedQueueSkip(
  input: { repository: string; pullNumber: number; headSha: string; reason?: string },
  skipped: boolean,
): Promise<void> {
  const session = await hostedSession();
  await apiJson("/api/queue/skips", {
    method: skipped ? "PUT" : "DELETE",
    headers: { "Content-Type": "application/json", "x-chew-csrf": session.csrfToken },
    body: JSON.stringify(input),
  });
  invalidateHostedQueueSnapshot();
}

/** Drop the viewer's own claim on a pull request from any surface, live review session or not. */
export async function releaseHostedPullClaim(
  input: { repository: string; pullNumber: number; headSha: string },
  reason: "unclaimed" | "skipped" | "handed-back" = "unclaimed",
): Promise<void> {
  const session = await hostedSession();
  await apiJson("/api/queue/claims", {
    method: "DELETE",
    headers: { "Content-Type": "application/json", "x-chew-csrf": session.csrfToken },
    body: JSON.stringify({ ...input, reason }),
  });
  invalidateHostedQueueSnapshot();
}

export type OutstandingPullStatus = "draft" | "changes-requested" | "approved" | "ready";

export interface OutstandingPull {
  repository: string;
  pullNumber: number;
  title: string;
  url: string;
  status: OutstandingPullStatus;
  requestedReviewers: string[];
  reviewers: string[];
  approvers: string[];
  loc: {
    changedLoc: number | null;
    effectiveLoc: number | null;
    reviewLoadLoc: number | null;
  };
  createdAt?: string;
  updatedAt?: string;
  reviewRequestedAt?: string;
  approvedAt?: string;
  approvedBy?: string[];
  changesRequestedAt?: string;
  changesRequestedBy?: string[];
  /** Reviewers the author has pushed past without asking back. */
  reReviewNeeded?: string[];
  attention: import("../../shared/pull-attention").PullAttentionSummary;
  headSha: string;
  authoring: {
    guideState: "missing" | "generating" | "draft" | "current" | "stale" | "failed";
    guideVersionId?: string;
    guideContentHash?: string;
    recommendation: {
      recommended: boolean;
      required: boolean;
      reasons: string[];
      effectiveChangedLoc: number;
      excludedChangedLoc: number;
      completeFileList: boolean;
    };
    codeSelfReview: "not-started" | "stale" | "current";
    /** Primary entry point for reviewing the author's own code. */
    url: string;
    /** Secondary workspace for shaping the generated reviewer guide. */
    editorUrl: string;
  };
}

export interface OutstandingPullSnapshot {
  source: "d1";
  freshness: {
    observedAt: string | null;
    stale: boolean;
    revalidating: boolean;
    lastErrorCode?: string;
  };
  items: OutstandingPull[];
}

let outstandingPullRevision = 0;
let outstandingPullRequest: { revision: number; refresh: boolean; promise: Promise<OutstandingPullSnapshot> } | undefined;
let outstandingPullSnapshot: { value: OutstandingPullSnapshot; expiresAt: number } | undefined;
// Monotonic fetch ordering: a slower, earlier request must never overwrite
// the cache with older data after a newer response already landed.
let outstandingPullFetchSequence = 0;
let outstandingPullStoredSequence = 0;

export function cachedOutstandingPulls(): OutstandingPull[] | undefined {
  return outstandingPullSnapshot?.value.items;
}

export function invalidateOutstandingPullSnapshot(): void {
  // Bumping the revision also makes any in-flight request discard its result,
  // so a poll started before a merge cannot resurrect pre-merge data.
  outstandingPullRevision += 1;
  outstandingPullSnapshot = undefined;
}

export async function loadOutstandingPullSnapshot(
  options: { refresh?: boolean } = {},
): Promise<OutstandingPullSnapshot> {
  if (!options.refresh && outstandingPullSnapshot && outstandingPullSnapshot.expiresAt > Date.now()) {
    return outstandingPullSnapshot.value;
  }
  const revision = outstandingPullRevision;
  // A refresh request must not piggyback on an in-flight cached read.
  if (outstandingPullRequest?.revision === revision && (outstandingPullRequest.refresh || !options.refresh)) {
    return outstandingPullRequest.promise;
  }
  const sequence = ++outstandingPullFetchSequence;
  const promise = hostedSession()
    .then(() => apiJson<OutstandingPullSnapshot>(options.refresh ? "/api/my-pulls?refresh=1" : "/api/my-pulls"))
    .then((response) => {
      if (outstandingPullRevision === revision && sequence > outstandingPullStoredSequence) {
        outstandingPullStoredSequence = sequence;
        outstandingPullSnapshot = { value: response, expiresAt: Date.now() + listCacheMilliseconds };
      }
      return response;
    })
    .finally(() => {
      if (outstandingPullRequest?.promise === promise) outstandingPullRequest = undefined;
    });
  outstandingPullRequest = { revision, refresh: Boolean(options.refresh), promise };
  return promise;
}

export async function loadOutstandingPulls(options: { refresh?: boolean } = {}): Promise<OutstandingPull[]> {
  return (await loadOutstandingPullSnapshot(options)).items;
}

export type MergeMethod = "merge" | "squash" | "rebase";

export interface MergeBlocker {
  code: string;
  detail: string;
}

export interface MergeReadiness {
  repository: string;
  pullNumber: number;
  title: string;
  headSha: string;
  baseRef: string;
  headRef: string;
  authorLogin: string;
  mergeable: boolean;
  blockers: MergeBlocker[];
  advisories: MergeBlocker[];
  githubState: string;
  review: {
    state: "approved" | "changes-requested" | "review-required" | "not-required" | "unavailable";
    approvals: number;
    requiredApprovals: number | null;
    approvers: string[];
    changesRequestedBy: string[];
    requestedReviewers: string[];
    codeOwnerReviewRequired: boolean;
  };
  ci: {
    state: "success" | "failure" | "pending" | "neutral" | "unavailable";
    total: number;
    succeeded: number;
    failed: number;
    pending: number;
  };
  methods: MergeMethod[];
  defaultMethod: MergeMethod | null;
  deleteBranchOnMerge: boolean;
  suggestedTitle: string;
}

export function loadMergeReadiness(repository: string, pullNumber: number): Promise<MergeReadiness> {
  return hostedSession().then(() => apiJson<MergeReadiness>(pullApiPath(repository, pullNumber, "merge")));
}

export async function mergeHostedPull(input: {
  repository: string;
  pullNumber: number;
  headSha: string;
  method: MergeMethod;
  title?: string;
}): Promise<{ merged: true; sha: string; method: MergeMethod; mergedAt: string }> {
  const session = await hostedSession();
  const result = await apiJson<{ merged: true; sha: string; method: MergeMethod; mergedAt: string }>(
    pullApiPath(input.repository, input.pullNumber, "merge"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CHEW-CSRF": session.csrfToken },
      body: JSON.stringify({ headSha: input.headSha, method: input.method, title: input.title }),
    },
  );
  invalidateOutstandingPullSnapshot();
  invalidateHostedQueueSnapshot();
  return result;
}

export type AnalyticsRange = "7d" | "30d" | "90d" | "all";
export type PersonalAnalyticsRange = Exclude<AnalyticsRange, "all">;
export type AnalyticsLocMetric = "changedLoc" | "effectiveLoc" | "reviewLoadLoc";
export type AnalyticsLocValues = Record<AnalyticsLocMetric, number | null>;

export interface PersonalAnalyticsMetric {
  value: number | null;
  previousValue: number | null;
  samples: number;
  previousSamples: number;
}

export interface PersonalAnalytics {
  range: PersonalAnalyticsRange;
  reportingTimeZone: string;
  generatedAt: string;
  collection: { startedAt: string; repositories: string[] };
  metrics: {
    reviewsCompleted: PersonalAnalyticsMetric;
    approvalsReceived: PersonalAnalyticsMetric;
    reviewLoad: PersonalAnalyticsMetric;
    medianResponseSeconds: PersonalAnalyticsMetric;
  };
  trend: Array<{
    period: string;
    reviewsCompleted: number;
    approvalsReceived: number;
    reviewLoad: number;
    medianResponseSeconds: number | null;
  }>;
}

export async function loadPersonalAnalytics(range: PersonalAnalyticsRange): Promise<PersonalAnalytics> {
  if (["localhost", "127.0.0.1", "::1"].includes(window.location.hostname)) {
    return localPersonalAnalyticsFixture(range);
  }
  await hostedSession();
  return apiJson<PersonalAnalytics>(`/api/analytics/me?range=${range}`);
}

function localPersonalAnalyticsFixture(range: PersonalAnalyticsRange): PersonalAnalytics {
  const now = new Date();
  const days = range === "7d" ? 7 : range === "30d" ? 30 : 90;
  const buckets = range === "90d" ? 13 : range === "30d" ? 15 : 7;
  const step = range === "90d" ? 7 : range === "30d" ? 2 : 1;
  const trend = Array.from({ length: buckets }, (_, index) => {
    const date = new Date(now.getTime() - (buckets - index - 1) * step * 86_400_000);
    const reviewsCompleted = [0, 1, 2, 0, 1, 1, 3][index % 7]!;
    const approvalsReceived = [1, 0, 1, 0, 2, 1, 1][index % 7]!;
    return {
      period: date.toISOString().slice(0, 10),
      reviewsCompleted,
      approvalsReceived,
      reviewLoad: reviewsCompleted * (180 + index * 19),
      medianResponseSeconds: reviewsCompleted ? 2_400 + (index % 4) * 700 : null,
    };
  });
  const currentReviews = trend.reduce((sum, bucket) => sum + bucket.reviewsCompleted, 0);
  const currentApprovals = trend.reduce((sum, bucket) => sum + bucket.approvalsReceived, 0);
  const reviewLoad = trend.reduce((sum, bucket) => sum + bucket.reviewLoad, 0);
  return {
    range,
    reportingTimeZone: "Pacific/Auckland",
    generatedAt: now.toISOString(),
    collection: {
      startedAt: new Date(now.getTime() - Math.max(120, days) * 86_400_000).toISOString(),
      repositories: ["Watchful-IP/Watchful", "Watchful-IP/chew"],
    },
    metrics: {
      reviewsCompleted: { value: currentReviews, previousValue: Math.max(0, currentReviews - 3), samples: currentReviews, previousSamples: Math.max(0, currentReviews - 3) },
      approvalsReceived: { value: currentApprovals, previousValue: Math.max(0, currentApprovals - 2), samples: currentApprovals, previousSamples: Math.max(0, currentApprovals - 2) },
      reviewLoad: { value: reviewLoad, previousValue: Math.round(reviewLoad * .82), samples: currentReviews, previousSamples: Math.max(1, currentReviews - 3) },
      medianResponseSeconds: { value: 3_420, previousValue: 4_260, samples: currentReviews, previousSamples: Math.max(1, currentReviews - 3) },
    },
    trend,
  };
}

export interface AnalyticsMetricComparison {
  value: number | null;
  previousValue: number | null;
  samples: number;
  previousSamples: number;
}

export interface AnalyticsDistribution {
  medianSeconds: number | null;
  p75Seconds: number | null;
  samples: number;
}

export interface AnalyticsDistributionComparison extends AnalyticsDistribution {
  previousMedianSeconds: number | null;
  previousP75Seconds: number | null;
  previousSamples: number;
}

export interface HostedAnalytics {
  range: AnalyticsRange;
  reportingTimeZone: string;
  collection: {
    startedAt: string;
    cutoff: string;
    previousCutoff: string | null;
    repositories: string[];
    sourceCoverage: { authored: number; unguided: number; pathInferred: number; missing: number };
  };
  metricDefinitions: Record<AnalyticsLocMetric, string>;
  totals: {
    mergedPulls: number;
    loc: AnalyticsLocValues;
    locSamples: Record<AnalyticsLocMetric, number>;
  };
  summary: {
    mergedPulls: AnalyticsMetricComparison;
    loc: Record<AnalyticsLocMetric, AnalyticsMetricComparison>;
    reviewerResponse: AnalyticsDistributionComparison;
    authorFollowUp: AnalyticsDistributionComparison;
  };
  coverage: {
    reviewerResponse: { samples: number; previousSamples: number; missingDirectRequestBaseline: number };
    authorFollowUp: { samples: number; previousSamples: number; pending: number; closedWithoutFollowUp: number };
  };
  trend: Array<{
    period: string;
    mergedPulls: number;
    loc: AnalyticsLocValues;
    locSamples: Record<AnalyticsLocMetric, number>;
    reviewerResponse: AnalyticsDistribution;
    authorFollowUp: AnalyticsDistribution;
  }>;
  authors: Array<{
    login: string;
    mergedPulls: number;
    loc: AnalyticsLocValues;
    authorFollowUp: AnalyticsDistribution;
  }>;
  reviewers: Array<{
    login: string;
    reviewerResponse: AnalyticsDistribution;
    completedReviews: number;
    completedChews: number;
    reviewedLoc: AnalyticsLocValues;
  }>;
  activeTime: {
    team: { averageSeconds: number | null; samples: number; reviewers: number } | null;
    minimumReviewers: number;
    me: { averageSeconds: number | null; totalSeconds: number; samples: number };
  };
  generatedAt: string;
}

export async function loadHostedAnalytics(
  range: AnalyticsRange,
  repository?: string,
): Promise<HostedAnalytics> {
  if (["localhost", "127.0.0.1", "::1"].includes(window.location.hostname)) {
    return localAnalyticsFixture(range, repository);
  }
  await hostedSession();
  const query = new URLSearchParams({ range });
  if (repository) query.set("repository", repository);
  return apiJson<HostedAnalytics>(`/api/analytics?${query}`);
}

function localAnalyticsFixture(range: AnalyticsRange, repository?: string): HostedAnalytics {
  const repositories = ["Watchful-IP/Watchful", "Watchful-IP/CHEW"];
  const selected = repository ? [repository] : repositories;
  const now = new Date();
  const bucketCount = range === "7d" ? 7 : range === "30d" ? 10 : 12;
  const bucketDays = range === "7d" ? 1 : range === "30d" ? 3 : 7;
  const flow = (seconds: number | null, samples: number): AnalyticsDistribution => ({
    medianSeconds: seconds,
    p75Seconds: seconds === null ? null : Math.round(seconds * 1.45),
    samples,
  });
  const trend = Array.from({ length: bucketCount }, (_, index) => {
    const date = new Date(now.getTime() - (bucketCount - index - 1) * bucketDays * 86_400_000);
    const mergedPulls = [1, 2, 0, 3, 1, 4, 2][index % 7]!;
    return {
      period: date.toISOString().slice(0, 10),
      mergedPulls,
      loc: {
        changedLoc: mergedPulls * (180 + index * 13),
        effectiveLoc: mergedPulls * (125 + index * 9),
        reviewLoadLoc: mergedPulls * (210 + index * 17),
      },
      locSamples: { changedLoc: mergedPulls, effectiveLoc: mergedPulls, reviewLoadLoc: mergedPulls },
      reviewerResponse: flow(mergedPulls ? 2_700 + index * 160 : null, mergedPulls ? Math.max(1, mergedPulls) : 0),
      authorFollowUp: flow(mergedPulls && index % 3 !== 2 ? 5_400 + index * 280 : null, mergedPulls && index % 3 !== 2 ? 1 : 0),
    };
  });
  const mergedPulls = selected.length === 1 ? 11 : 18;
  const loc = { changedLoc: 5_940, effectiveLoc: 4_120, reviewLoadLoc: 7_280 };
  const locSamples = { changedLoc: 18, effectiveLoc: 18, reviewLoadLoc: 18 };
  const comparison = (value: number | null, samples: number): AnalyticsMetricComparison => ({
    value,
    previousValue: range === "all" || value === null ? null : Math.round(value * .82),
    samples,
    previousSamples: range === "all" ? 0 : Math.max(0, samples - 3),
  });
  return {
    range,
    reportingTimeZone: "Pacific/Auckland",
    collection: {
      startedAt: new Date(now.getTime() - 46 * 86_400_000).toISOString(),
      cutoff: trend[0]?.period ?? now.toISOString(),
      previousCutoff: range === "all" ? null : new Date(now.getTime() - bucketCount * bucketDays * 2 * 86_400_000).toISOString(),
      repositories,
      sourceCoverage: { authored: 19, unguided: 7, pathInferred: 2, missing: 1 },
    },
    metricDefinitions: {
      changedLoc: "Additions plus deletions.",
      effectiveLoc: "Changed LOC excluding configured tests and generated output.",
      reviewLoadLoc: "Changed LOC weighted by declared scrutiny, demanding concerns, and exclusions.",
    },
    totals: {
      mergedPulls,
      loc,
      locSamples,
    },
    summary: {
      mergedPulls: comparison(mergedPulls, mergedPulls),
      loc: {
        changedLoc: comparison(loc.changedLoc, locSamples.changedLoc),
        effectiveLoc: comparison(loc.effectiveLoc, locSamples.effectiveLoc),
        reviewLoadLoc: comparison(loc.reviewLoadLoc, locSamples.reviewLoadLoc),
      },
      reviewerResponse: { ...flow(6_360, 21), previousMedianSeconds: range === "all" ? null : 7_120, previousP75Seconds: range === "all" ? null : 10_300, previousSamples: range === "all" ? 0 : 18 },
      authorFollowUp: { ...flow(9_180, 12), previousMedianSeconds: range === "all" ? null : 10_440, previousP75Seconds: range === "all" ? null : 15_200, previousSamples: range === "all" ? 0 : 10 },
    },
    coverage: {
      reviewerResponse: { samples: 21, previousSamples: range === "all" ? 0 : 18, missingDirectRequestBaseline: 3 },
      authorFollowUp: { samples: 12, previousSamples: range === "all" ? 0 : 10, pending: 2, closedWithoutFollowUp: 1 },
    },
    trend,
    authors: [
      { login: "alex", mergedPulls: 6, loc: { changedLoc: 1_940, effectiveLoc: 1_410, reviewLoadLoc: 2_460 }, authorFollowUp: flow(7_260, 5) },
      { login: "morgan", mergedPulls: 5, loc: { changedLoc: 1_610, effectiveLoc: 1_120, reviewLoadLoc: 1_980 }, authorFollowUp: flow(12_540, 4) },
      { login: "sam", mergedPulls: 7, loc: { changedLoc: 2_390, effectiveLoc: 1_590, reviewLoadLoc: 2_840 }, authorFollowUp: flow(9_180, 6) },
    ],
    reviewers: [
      { login: "alex", reviewerResponse: flow(4_680, 8), completedReviews: 9, completedChews: 9, reviewedLoc: { changedLoc: 2_820, effectiveLoc: 1_940, reviewLoadLoc: 3_410 } },
      { login: "morgan", reviewerResponse: flow(8_160, 6), completedReviews: 7, completedChews: 7, reviewedLoc: { changedLoc: 2_140, effectiveLoc: 1_520, reviewLoadLoc: 2_890 } },
      { login: "sam", reviewerResponse: flow(6_360, 7), completedReviews: 8, completedChews: 8, reviewedLoc: { changedLoc: 2_560, effectiveLoc: 1_780, reviewLoadLoc: 3_120 } },
    ],
    activeTime: {
      team: { averageSeconds: 1_140, samples: 24, reviewers: 3 },
      minimumReviewers: 3,
      me: { averageSeconds: 1_080, totalSeconds: 9_720, samples: 9 },
    },
    generatedAt: now.toISOString(),
  };
}

export interface HostedBootstrap {
  mode: "live";
  reviewAccess: ReviewAccess;
  chewfile: {
    status: "guided" | "description" | "unguided";
    message: string;
    guideVersionId: string;
  };
  guide: ReviewGuide;
  compilation: CodeMapCompilation;
  reviewSession: {
    id: string;
    status: "active" | "stale" | "published";
    claim?: {
      status: "owned" | "held";
      slotKey: string;
      slotLabel: string;
      reviewerLogin: string;
      claimedAt: string;
      lastActiveAt: string;
      expiresAt: string;
    };
  };
  pull: {
    number: number;
    title: string;
    body?: string | null;
    state: "open" | "closed";
    draft: boolean;
    url: string;
    author: string;
    assignees: string[];
    requestedReviewers: string[];
    requestedTeams: string[];
    labels: string[];
    createdAt?: string;
    updatedAt?: string;
    baseRef: string;
    headRef: string;
    baseSha: string;
    headSha: string;
    additions: number;
    deletions: number;
    changedFiles: number;
    commits: number;
  };
  files: Array<{
    path: string;
    previousPath?: string;
    status: string;
    headBlobSha: string;
  }>;
  warnings: Array<{ code: string; message: string }>;
}

export type HostedPreview = Omit<HostedBootstrap, "reviewSession">;

export interface HostedReviewThread {
  id: string;
  path: string;
  side?: "LEFT" | "RIGHT";
  line?: number;
  startLine?: number;
  resolved: boolean;
  outdated: boolean;
  comments: HostedConversationComment[];
}

export interface HostedReviewContext {
  headSha: string;
  resolution: {
    review: {
      state: "approved" | "changes-requested" | "review-required" | "not-required" | "unavailable";
      approvals: number;
      requiredApprovals: number | null;
      approvers: string[];
      changesRequestedBy: string[];
      requestedReviewers: string[];
      codeOwnerReviewRequired: boolean;
    };
    merge: {
      draft: boolean;
      mergeable: boolean | null;
      state: string;
      conversationResolutionRequired: boolean;
    };
  };
  authorSweep: {
    status: "none" | "current" | "stale";
    author?: string;
    reviewedHeadSha?: string;
    completedAt?: string;
    completedLayers?: number;
    totalLayers?: number;
    visitedFocalStops?: number;
  };
  ci: {
    state: "success" | "failure" | "pending" | "neutral" | "unavailable";
    total: number;
    succeeded: number;
    failed: number;
    pending: number;
    unavailableReason?: string;
    checks: Array<{
      id: string;
      name: string;
      state: "success" | "failure" | "pending" | "neutral";
      url: string | null;
      source: "check-run" | "commit-status";
    }>;
  };
  conversation: {
    issueCommentCount: number;
    reviewThreadCount: number;
    unresolvedThreadCount: number;
    outdatedThreadCount: number;
    truncated: boolean;
    completeness?: {
      issueComments: { total: number; collected: number; truncated: boolean };
      reviewThreads: { total: number; collected: number; truncated: boolean };
      threadReplies: { truncatedThreads: number; omitted: number };
      recent: { total: number; collected: number; truncated: boolean };
      bodies?: { shortened: number; limit: number };
    };
    unavailableReason?: string;
    threads?: HostedReviewThread[];
    recent: Array<{
      kind: "conversation" | "review-thread";
      author: string;
      authorType: string;
      body: string;
      createdAt: string;
      url: string;
      path?: string;
      side?: "LEFT" | "RIGHT";
      line?: number;
      resolved?: boolean;
      outdated?: boolean;
    }>;
  };
  changedSinceReview: {
    status: "never-reviewed" | "current" | "changed" | "unavailable";
    reviewedAt?: string;
    reviewedHeadSha?: string;
    commits: number;
    additions: number;
    deletions: number;
    truncated: boolean;
    files: Array<{
      path: string;
      previousPath?: string;
      status: string;
      additions: number;
      deletions: number;
    }>;
    basis?: "direct" | "current-pr-files";
    message?: string;
  };
}

export interface HostedConversationComment {
  id: number;
  author: string;
  body: string;
  createdAt: string;
  url: string;
}

const previewPromises = new Map<string, Promise<HostedPreview>>();
const contextPromises = new Map<string, Promise<HostedReviewContext>>();
const sourcePromises = new Map<string, Promise<HostedSourceFile>>();

export function pullApiPath(repository: string, pullNumber: number, action: string): string {
  const [owner, repo] = repository.split("/");
  return `/api/pulls/${encodeURIComponent(owner!)}/${encodeURIComponent(repo!)}/${pullNumber}/${action}`;
}

function cachedRequest<T>(cache: Map<string, Promise<T>>, key: string, request: () => Promise<T>): Promise<T> {
  const existing = cache.get(key);
  if (existing) return existing;
  const pending = request().catch((error: unknown) => {
    cache.delete(key);
    throw error;
  });
  cache.set(key, pending);
  window.setTimeout(() => {
    if (cache.get(key) === pending) cache.delete(key);
  }, 60_000);
  return pending;
}

export async function previewHostedPull(repository: string, pullNumber: number): Promise<HostedPreview> {
  await hostedSession();
  const key = `${repository}#${pullNumber}`;
  return cachedRequest(previewPromises, key, async () => {
    performance.mark?.(`chew:preview:${key}:start`);
    const preview = await apiJson<HostedPreview>(pullApiPath(repository, pullNumber, "preview"));
    performance.mark?.(`chew:preview:${key}:ready`);
    return preview;
  });
}

export interface HostedGuideGeneration {
  id: string;
  trigger: "label-request" | "manual-refresh";
  targetHeadSha: string;
  status: "queued" | "running" | "succeeded" | "failed" | "superseded";
  publicationMode: "draft" | "publish";
  draftId?: string;
  workflowUrl?: string;
  engine?: string;
  model?: string;
  failure?: { code: string; detail?: string };
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

export interface HostedGuideEditor {
  state: "missing" | "generating" | "draft" | "current" | "stale" | "failed";
  pull: {
    repository: string;
    number: number;
    title: string;
    url: string;
    author: string;
    baseSha: string;
    headSha: string;
    baseRef: string;
    headRef: string;
  };
  guideVersion?: {
    id: string;
    headSha: string;
    revisionId: string | null;
    importedAt: string;
    publication: "draft" | "published";
    guide: ReviewGuide;
    compilation: CodeMapCompilation;
    reviewActivityExists: boolean;
  };
  generation?: HostedGuideGeneration;
  files: Array<{
    path: string;
    previousPath?: string;
    status: string;
    additions: number;
    deletions: number;
    changes: number;
    patch?: string;
    truncated: boolean;
  }>;
  annotations: Array<{
    id: string;
    revisionId?: string;
    visibility: "reviewer-note" | "regeneration-instruction";
    anchor:
      | { kind: "diff-range"; path: string; side: "LEFT" | "RIGHT"; startLine: number; endLine: number }
      | { kind: "guide-field"; fieldPath: string }
      | { kind: "global" };
    body: string;
    author: string;
    createdAt: string;
    updatedAt: string;
  }>;
  staleness?: {
    regions: Array<{ regionId: string; layerId?: string; path: string; state: "current" | "moved" | "stale" | "removed"; startLine?: number; endLine?: number; reason?: string }>;
    staleLayerIds: string[];
    newPatches: Array<{ path: string; side: "LEFT" | "RIGHT"; startLine?: number; endLine?: number; truncated?: boolean }>;
    counts: { current: number; moved: number; stale: number; removed: number; new: number };
  };
}

export async function loadHostedGuideEditor(repository: string, pullNumber: number): Promise<HostedGuideEditor> {
  await hostedSession();
  return apiJson<HostedGuideEditor>(pullApiPath(repository, pullNumber, "guide-editor"));
}

export async function saveHostedGuideEditor(input: {
  repository: string;
  pullNumber: number;
  expectedRevisionId: string;
  expectedHeadSha: string;
  guide: ReviewGuide;
}): Promise<{ ok: true; published: boolean; revisionId: string; revisionNumber: number; guide: ReviewGuide }> {
  const session = await hostedSession();
  const result = await apiJson<{ ok: true; published: boolean; revisionId: string; revisionNumber: number; guide: ReviewGuide }>(
    pullApiPath(input.repository, input.pullNumber, "guide-editor"),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json", "X-CHEW-CSRF": session.csrfToken },
      body: JSON.stringify({
        expectedRevisionId: input.expectedRevisionId,
        expectedHeadSha: input.expectedHeadSha,
        guide: input.guide,
      }),
    },
  );
  previewPromises.clear();
  invalidateOutstandingPullSnapshot();
  return result;
}

export async function publishHostedGuideDraft(input: {
  repository: string;
  pullNumber: number;
  expectedHeadSha: string;
  expectedRevisionId: string;
}): Promise<{ ok: true; published: true; guideVersionId: string; revisionId: string }> {
  const session = await hostedSession();
  const result = await apiJson<{ ok: true; published: true; guideVersionId: string; revisionId: string }>(
    pullApiPath(input.repository, input.pullNumber, "guide-editor/publish"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CHEW-CSRF": session.csrfToken },
      body: JSON.stringify({ expectedHeadSha: input.expectedHeadSha, expectedRevisionId: input.expectedRevisionId }),
    },
  );
  previewPromises.clear();
  invalidateOutstandingPullSnapshot();
  return result;
}

export async function addHostedGuideAnnotation(input: {
  repository: string;
  pullNumber: number;
  expectedHeadSha: string;
  expectedRevisionId?: string | null;
  visibility: "reviewer-note" | "regeneration-instruction";
  anchor: HostedGuideEditor["annotations"][number]["anchor"];
  body: string;
}): Promise<{ ok: true; id: string }> {
  const session = await hostedSession();
  const result = await apiJson<{ ok: true; id: string }>(pullApiPath(input.repository, input.pullNumber, "guide-editor/annotations"), {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CHEW-CSRF": session.csrfToken },
    body: JSON.stringify(input),
  });
  previewPromises.clear();
  return result;
}

export async function dismissHostedGuideAnnotation(input: {
  repository: string;
  pullNumber: number;
  annotationId: string;
  expectedHeadSha: string;
  expectedRevisionId?: string | null;
}): Promise<{ ok: true }> {
  const session = await hostedSession();
  const result = await apiJson<{ ok: true }>(`${pullApiPath(input.repository, input.pullNumber, "guide-editor/annotations")}/${encodeURIComponent(input.annotationId)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", "X-CHEW-CSRF": session.csrfToken },
    body: JSON.stringify({ expectedHeadSha: input.expectedHeadSha, expectedRevisionId: input.expectedRevisionId }),
  });
  previewPromises.clear();
  return result;
}

export async function regenerateHostedGuide(input: {
  repository: string;
  pullNumber: number;
  expectedHeadSha: string;
  expectedRevisionId?: string | null;
}): Promise<{ created: boolean; generation: HostedGuideGeneration }> {
  const session = await hostedSession();
  return apiJson(pullApiPath(input.repository, input.pullNumber, "guide-editor/regenerate"), {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CHEW-CSRF": session.csrfToken },
    body: JSON.stringify({ expectedHeadSha: input.expectedHeadSha, expectedRevisionId: input.expectedRevisionId }),
  });
}

export async function loadHostedPullContext(repository: string, pullNumber: number): Promise<HostedReviewContext> {
  await hostedSession();
  const key = `${repository}#${pullNumber}`;
  return cachedRequest(contextPromises, key, () =>
    apiJson<HostedReviewContext>(pullApiPath(repository, pullNumber, "context"))
  );
}

export async function startHostedPullReview(
  repository: string,
  pullNumber: number,
  headSha: string,
): Promise<Pick<HostedBootstrap, "reviewSession"> & { headSha: string }> {
  const session = await hostedSession();
  try {
    return await apiJson(pullApiPath(repository, pullNumber, "start"), {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CHEW-CSRF": session.csrfToken },
      body: JSON.stringify({ headSha }),
    });
  } catch (error) {
    if (error instanceof HostedApiError && error.code === "stale_preview") {
      const key = `${repository}#${pullNumber}`;
      previewPromises.delete(key);
      contextPromises.delete(key);
    }
    throw error;
  }
}

export async function preloadHostedPull(repository: string, pullNumber: number): Promise<void> {
  const [preview] = await Promise.all([
    previewHostedPull(repository, pullNumber),
    loadHostedPullContext(repository, pullNumber).catch(() => undefined),
  ]);
  const regions = new Map(preview.guide.codeMap.regions.map((region) => [region.id, region]));
  const firstLayerPaths = [...new Set(preview.guide.layers[0]?.items.flatMap((item) =>
    item.regionIds.map((id) => regions.get(id)?.path).filter(Boolean) as string[]
  ) ?? [])].slice(0, 2);
  await Promise.all(firstLayerPaths.map((path) =>
    loadHostedSourceFile(repository, pullNumber, preview.pull.headSha, path).catch(() => undefined)
  ));
}

export async function bootstrapHostedPull(repository: string, pullNumber: number): Promise<HostedBootstrap> {
  await hostedSession();
  return apiJson(pullApiPath(repository, pullNumber, "bootstrap"));
}

export interface HostedSourceFile {
  path: string;
  oldPath: string;
  status: string;
  encoding: "utf8" | "base64";
  binary: boolean;
  base: string;
  head: string;
  headBlobSha: string;
}

export async function loadHostedSourceFile(
  repository: string,
  pullNumber: number,
  headSha: string,
  path: string,
): Promise<HostedSourceFile> {
  const key = `${repository}#${pullNumber}@${headSha}:${path}`;
  return cachedRequest(sourcePromises, key, () => apiJson(
    `${pullApiPath(repository, pullNumber, "file")}?path=${encodeURIComponent(path)}&head=${encodeURIComponent(headSha)}`,
  ));
}

export interface HostedInlineComment {
  path: string;
  body: string;
  type: DraftCommentKind;
  side: "LEFT" | "RIGHT";
  /** Last line of the comment, matching GitHub's multi-line comment semantics. */
  line: number;
  /** Both range fields travel together; GitHub rejects one without the other. */
  startLine?: number;
  startSide?: "LEFT" | "RIGHT";
}

export function hostedInlineComment(anchor: DraftAnchor, body: string, type: DraftCommentKind): HostedInlineComment {
  const { startLine, endLine } = draftAnchorSpan(anchor);
  return {
    path: anchor.path,
    body,
    type,
    side: anchor.side,
    line: endLine,
    ...(startLine === endLine ? {} : { startLine, startSide: anchor.side }),
  };
}

export async function publishHostedReview(input: {
  sessionId: string;
  repository: string;
  pullNumber: number;
  headSha: string;
  outcome: "approve" | "request-changes" | "comment" | "not-ready";
  selfReview?: boolean;
  body: string;
  comments: HostedInlineComment[];
  activeSeconds: number;
}): Promise<{ reviewId: number; reviewUrl: string; duplicate: boolean }> {
  const session = await hostedSession();
  const event = githubReviewEventFor(input.outcome, input.selfReview ? "author" : "reviewer");
  const body = input.outcome === "not-ready"
    ? `Not ready for review\n\n${input.body}`.trim()
    : input.body;
  const response = await apiJson<{ reviewId: number; reviewUrl: string; duplicate: boolean }>(`/api/review-sessions/${encodeURIComponent(input.sessionId)}/publish`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CHEW-CSRF": session.csrfToken },
    body: JSON.stringify({
      headSha: input.headSha,
      event,
      outcome: input.outcome,
      body,
      comments: input.comments,
      activeSeconds: input.activeSeconds,
    }),
  });
  locallyCompletedQueueHeads.add(queueHeadKey(input.repository, input.pullNumber, input.headSha));
  invalidateHostedQueueSnapshot();
  return response;
}

export async function attestHostedAuthorSweep(input: {
  sessionId: string;
  headSha: string;
  markReady: boolean;
}): Promise<{
  attested: true;
  duplicate: boolean;
  headSha: string;
  completedAt: string;
  coverage: { completedLayers: number; totalLayers: number; visitedFocalStops: number };
  readyMarked: boolean;
  label: string;
}> {
  const session = await hostedSession();
  const response = await apiJson<{
    attested: true;
    duplicate: boolean;
    headSha: string;
    completedAt: string;
    coverage: { completedLayers: number; totalLayers: number; visitedFocalStops: number };
    readyMarked: boolean;
    label: string;
  }>(`/api/review-sessions/${encodeURIComponent(input.sessionId)}/author-attestation`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CHEW-CSRF": session.csrfToken },
    body: JSON.stringify({
      headSha: input.headSha,
      confirmation: true,
      markReady: input.markReady,
    }),
  });
  invalidateHostedQueueSnapshot();
  return response;
}

export async function replyToHostedReviewThread(input: {
  sessionId: string;
  headSha: string;
  commentId: number;
  body: string;
}): Promise<HostedConversationComment> {
  const session = await hostedSession();
  const response = await apiJson<{ comment: HostedConversationComment }>(
    `/api/review-sessions/${encodeURIComponent(input.sessionId)}/thread-replies`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CHEW-CSRF": session.csrfToken },
      body: JSON.stringify({ headSha: input.headSha, commentId: input.commentId, body: input.body }),
    },
  );
  return response.comment;
}

export async function appendHostedReviewEvents(
  sessionId: string,
  events: ReviewEvent[],
): Promise<void> {
  if (events.length === 0) return;
  const session = await hostedSession();
  await apiJson(`/api/review-sessions/${encodeURIComponent(sessionId)}/events`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CHEW-CSRF": session.csrfToken },
    body: JSON.stringify({ events: events.map((event) => ({ ...event, sessionId })) }),
  });
}

export async function heartbeatHostedReviewClaim(sessionId: string): Promise<"owned" | "lost"> {
  const session = await hostedSession();
  const response = await apiJson<{ claim: { status: "owned" | "lost" } }>(
    `/api/review-sessions/${encodeURIComponent(sessionId)}/heartbeat`,
    {
      method: "POST",
      headers: { "X-CHEW-CSRF": session.csrfToken },
    },
  );
  return response.claim.status;
}

export async function releaseHostedReviewClaim(
  sessionId: string,
  reason: "left-review" | "skipped" | "not-ready" | "published" = "left-review",
): Promise<void> {
  const session = await hostedSession();
  await apiJson(`/api/review-sessions/${encodeURIComponent(sessionId)}/release`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CHEW-CSRF": session.csrfToken },
    body: JSON.stringify({ reason }),
    keepalive: true,
  });
}

export interface PullStatusItem {
  repository: string;
  pullNumber: number;
  title: string;
  url: string;
  status: OutstandingPullStatus;
  activityAt?: string;
  attention: import("../../shared/pull-attention").PullAttentionSummary;
}

export interface ReviewRequestItem {
  repository: string;
  pullNumber: number;
  title: string;
  authorLogin: string;
  headSha: string;
  requestedAt: string;
}

export interface MyPullInbox {
  statuses: PullStatusItem[];
  reviewRequests: ReviewRequestItem[];
}

// Webhook-fed D1 read on the worker; costs no GitHub rate limit, safe to poll.
export async function loadMyPullInbox(): Promise<MyPullInbox> {
  const session = await hostedSession();
  try {
    const response = await apiJson<{
      source: "d1";
      items: PullStatusItem[];
      reviewRequests?: ReviewRequestItem[];
    }>("/api/my-pulls/statuses");
    const inbox = { statuses: response.items, reviewRequests: response.reviewRequests ?? [] };
    rememberLastKnownGood(session.user.login, "inbox", inbox);
    servedFresh("inbox");
    return inbox;
  } catch (reason: unknown) {
    if (hostedAuthenticationRequired(reason)) forgetLastKnownGood();
    const remembered = unreachable(reason)
      ? readLastKnownGood<MyPullInbox>(session.user.login, "inbox")
      : undefined;
    if (!remembered) throw reason;
    servedStale("inbox", remembered.storedAt);
    return remembered.value;
  }
}
